import { BuildingRestaurant, CaretRight } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

type Item = {
  id: string;
  name: string;
  address: string;
  url: string;
  status: 'READY_TO_SUBMIT' | 'VENUE_CREATED' | string;
};

type Props = {
  className?: string;
  item: Item;
};

const Root = styled.div``;

const StyledLink = styled(Link)`
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  color: ${cbSemanticCssVars.textPrimary};
  text-decoration: none;
  border-radius: 16px;
  &:hover {
    background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};
  }
`;

const DisabledLink = styled.div`
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  color: ${cbSemanticCssVars.textPrimary};
  text-decoration: none;
  border-radius: 16px;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-inline-end: 8px;
  background-color: ${cssThemedValue({ light: colors.pepper12, dark: colors.salt12 })};
  border-radius: 50%;
`;

const ImagePlaceholder = styled(BuildingRestaurant)`
  flex: 0 0 auto;
  fill: ${cbSemanticCssVars.textTertiary};
  width: 24px;
  height: auto;
`;

const InfoContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Name = styled(T.Body2).attrs({ fontWeight: 'semiBold' })`
  display: block;
  margin-block-end: 4px;
`;

const AddressAndStatus = styled(T.Body3)`
  display: flex;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
`;

const CaretIcon = styled(CaretRight)`
  flex: 0 0 auto;
  fill: ${cbSemanticCssVars.textTertiary};
  width: 16px;
  height: auto;
`;

const Status = styled(Tag)`
  margin-inline-start: 8px;
`;

const DraftVenueListItem = ({ className, item }: Props) => {
  const statusMessageId: string | undefined =
    item.status === 'READY_TO_SUBMIT'
      ? 'venue-data-collection.start.ready-to-submit'
      : item.status === 'VENUE_CREATED'
        ? 'venue-data-collection.start.venue-created'
        : undefined;

  const isVenueCreated = item.status === 'VENUE_CREATED';

  return (
    <Root>
      <StyledLink
        to={item.url}
        className={className}
        data-test-id="venue-data-collection-draft-venue-list-item"
        component={isVenueCreated ? ({ children }) => <DisabledLink> {children}</DisabledLink> : undefined}
      >
        <ImageContainer>
          <ImagePlaceholder />
        </ImageContainer>
        <InfoContainer>
          <Name>{item.name}</Name>
          <AddressAndStatus>
            {item.address}
            {statusMessageId && (
              <Status variant={isVenueCreated ? 'green' : 'blue'} size="tiny" uppercase>
                <FormattedMessage id={statusMessageId} />
              </Status>
            )}
          </AddressAndStatus>
        </InfoContainer>
        {!isVenueCreated && <CaretIcon />}
      </StyledLink>
    </Root>
  );
};

export default DraftVenueListItem;
